export type RoutineExercise = { name: string; default_sets: number };
export type ExerciseDraft = { key: string; name: string; sets: number; warmups: number; reps: string; rest: number; group: number };
/**
 * The rest timer is held as a deadline, not as a number that something decrements.
 *
 * A phone suspends a backgrounded page's timers, so an interval that subtracts a second
 * per tick simply stops counting the moment you switch apps and resumes from where it
 * froze — the rest looked like it lasted 40 seconds when it lasted four minutes.
 * `endsAt` is wall-clock milliseconds, so the remaining time is correct however long the
 * page was asleep; `remaining` is a cache of it for rendering, and the only authority
 * while paused, when `endsAt` is 0.
 */
export type RestState = { target: number; endsAt: number; remaining: number };
export type RestAction =
  | { type: "sync"; now: number }
  | { type: "start"; target?: number; now: number }
  | { type: "toggle"; now: number }
  | { type: "reset" }
  | { type: "adjust"; delta: number; now: number };

export const REST_MIN = 15;
export const REST_MAX = 600;
export const REST_DEFAULT = 90;

/** Where the timer is kept, so a rest survives the page being discarded mid-workout. */
export const REST_STORAGE_KEY = "rubymax-rest";

/**
 * How stale a passed deadline may be and still ring. A rest that ended while you were
 * reading a message deserves its alarm; one from a workout two hours ago does not, and
 * coming back to a screen insisting rest is over is worse than coming back to a fresh timer.
 */
export const REST_RESUME_GRACE_MS = 60_000;

export const restRunning = (state: RestState) => state.endsAt > 0;

/** Seconds left, rounded up so a fresh 90-second rest reads 01:30 rather than 01:29. */
const secondsUntil = (endsAt: number, now: number) => Math.max(0, Math.ceil((endsAt - now) / 1000));

/** Local-calendar ISO day. `toISOString()` converts to UTC first, which lands on the wrong day east of UTC+12. */
export const isoDay = (value: Date) => `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
export const today = (offset = 0) => { const value = new Date(); value.setDate(value.getDate() + offset); return isoDay(value); };
export const shiftDay = (value: string, offset: number) => { const current = new Date(`${value}T12:00:00`); current.setDate(current.getDate() + offset); return isoDay(current); };

/** Whole weeks ending on the Saturday of `anchor`'s week, so the 7-wide grid actually lines up with weekdays. */
export function calendarRange(anchor: string, weeks = 5): string[] {
  const end = new Date(`${anchor}T12:00:00`);
  end.setDate(end.getDate() + (6 - end.getDay()));
  const cells: string[] = [];
  for (let back = weeks * 7 - 1; back >= 0; back -= 1) {
    const value = new Date(end); value.setDate(value.getDate() - back);
    cells.push(isoDay(value));
  }
  return cells;
}

/** Note the Number.isFinite check rather than a falsy test: decrementing 15 by 15 gives 0,
 *  which must clamp to the floor, not be mistaken for an unset value. */
export const clampRest = (seconds: number) => {
  const value = Math.round(seconds);
  return Number.isFinite(value) ? Math.min(REST_MAX, Math.max(REST_MIN, value)) : REST_DEFAULT;
};

/** A routine's exercises as the editor holds them, before they go back to the API. */
export function toDrafts(exercises: Array<Record<string, unknown>> = []): ExerciseDraft[] {
  return exercises.map((exercise, index) => ({
    key: String(exercise.id ?? index),
    name: String(exercise.name ?? ""),
    sets: Math.max(1, Number(exercise.default_sets) || 3),
    warmups: Math.max(0, Math.min(10, Number(exercise.warmup_sets) || 0)),
    reps: String(exercise.target_reps ?? ""),
    rest: clampRest(Number(exercise.rest_seconds) || REST_DEFAULT),
    group: Math.max(0, Math.min(26, Number(exercise.superset_group) || 0))
  }));
}

export function fromDrafts(drafts: ExerciseDraft[]) {
  return drafts.filter((draft) => draft.name.trim()).map((draft) => ({
    name: draft.name.trim(),
    default_sets: Math.max(1, Math.round(draft.sets) || 3),
    warmup_sets: Math.max(0, Math.min(10, Math.round(draft.warmups) || 0)),
    target_reps: draft.reps.trim(),
    rest_seconds: clampRest(draft.rest),
    superset_group: Math.max(0, Math.min(26, Math.round(draft.group) || 0))
  }));
}

export const isWarmup = (set: { set_type?: unknown } | undefined) => String(set?.set_type ?? "working") === "warmup";

export const groupLetter = (group: number) => group > 0 ? String.fromCharCode(64 + group) : "";

/**
 * Consecutive exercises sharing a non-zero group are one superset block.
 * Non-consecutive matches are deliberately separate blocks — you cannot superset
 * two exercises with an unrelated one performed in between.
 */
export function groupExercises<T extends { superset_group?: unknown }>(exercises: T[]): Array<{ group: number; items: T[] }> {
  return exercises.reduce<Array<{ group: number; items: T[] }>>((blocks, exercise) => {
    const group = Math.max(0, Number(exercise.superset_group) || 0);
    const last = blocks.at(-1);
    if (group > 0 && last && last.group === group) last.items.push(exercise);
    else blocks.push({ group, items: [exercise] });
    return blocks;
  }, []);
}

/** Working sets are numbered 1..n. Warm-ups show W and do not consume a number. */
export function setLabels(sets: Array<{ set_type?: unknown }>): string[] {
  let working = 0;
  return sets.map((set) => isWarmup(set) ? "W" : String(++working));
}

/**
 * Line each current set up with the equivalent one from last time — third working set
 * against last third working set, not against whatever happened to be third overall.
 * Matching on raw index would shift every row when a warm-up is added or dropped.
 */
export function priorForSets<T extends { set_type?: unknown }>(prior: T[], current: Array<{ set_type?: unknown }>): Array<T | undefined> {
  const warmups = prior.filter((set) => isWarmup(set));
  const working = prior.filter((set) => !isWarmup(set));
  let warmupIndex = 0; let workingIndex = 0;
  return current.map((set) => isWarmup(set) ? warmups[warmupIndex++] : working[workingIndex++]);
}

/** Warm-ups are excluded: they would flatter volume and top-weight trends. */
export function workingVolume(sets: Array<{ set_type?: unknown; weight?: unknown; reps?: unknown }>): number {
  return sets.filter((set) => !isWarmup(set)).reduce((sum, set) => sum + (Number(set.weight) || 0) * (Number(set.reps) || 0), 0);
}

export function topWorkingWeight(sets: Array<{ set_type?: unknown; weight?: unknown }>): number {
  return sets.filter((set) => !isWarmup(set)).reduce((top, set) => Math.max(top, Number(set.weight) || 0), 0);
}

/**
 * The set to beat for one exercise: the heaviest weight ever put up, and the best reps at it.
 *
 * `on` is the day it was first done, and is absent only where the caller supplied no date.
 */
export type SetRecord = { weight: number; reps: number; on?: string };
type LoggedSet = { set_type?: unknown; weight?: unknown; reps?: unknown; completed?: unknown };

/**
 * The winner of `best` against a new weight × reps.
 *
 * Heavier always wins, and the same weight wins on more reps. Deliberately neither an
 * estimated one-rep max nor a volume total: this is the claim you can make standing at
 * the rack — nothing heavier has gone up before — rather than a number needing a formula
 * to explain. An unfinished row (no weight or no reps yet) beats nothing.
 */
function better(best: SetRecord | undefined, weight: number, reps: number): SetRecord | undefined {
  if (weight <= 0 || reps <= 0) return best;
  if (!best || weight > best.weight) return { weight, reps };
  return weight === best.weight && reps > best.reps ? { weight, reps } : best;
}

/**
 * A set that happened: a working set that was actually ticked off.
 *
 * A row filled in but never ticked did not happen, and warm-ups are excluded for the same
 * reason they are excluded from volume and top weight. History carries `completed` as 0 or 1
 * and sets being logged carry it as a boolean; both read the same here. Stored history has
 * always had the field, so its absence means a caller's own shape, taken as done.
 */
const countsAsWork = (set: LoggedSet) => !isWarmup(set) && (set.completed === undefined || Boolean(Number(set.completed)));
const volumeOf = (set: LoggedSet) => (Number(set.weight) || 0) * (Number(set.reps) || 0);

/** What a set can beat. */
export type RecordKind = "weight" | "reps" | "set volume" | "exercise volume";

/**
 * The bars one exercise sets for itself.
 *
 * `best` carries two of them: the heaviest weight ever put up — the claim you can make
 * standing at the rack, and the one that does not care how many reps followed — and the most
 * reps anyone has held that weight for, which is the only way to beat it without loading
 * the bar. `setVolume` is the most work done in a single set, which is how a longer set at a
 * weight you outgrew still counts for something. `sessionVolume` is the most work the
 * exercise took across one workout, and is the only record no single set can win alone.
 */
export type VolumeRecord = { volume: number; on?: string };
export type ExerciseRecords = { best?: SetRecord; setVolume: VolumeRecord; sessionVolume: VolumeRecord };

type LoggedWorkout = { performed_on?: unknown; exercises?: Array<{ name?: unknown; sets?: LoggedSet[] }> };

/**
 * Every exercise's records, keyed by lower-cased name, as all history matching in this app is.
 *
 * Workouts are walked oldest first, whatever order they arrive in, so a record is dated the
 * day it was *first* reached rather than the last day it was matched. History comes back
 * newest first, and taking that order would have dated a two-year-old best to last Tuesday.
 */
export function exerciseRecords(workouts: LoggedWorkout[] = []): Map<string, ExerciseRecords> {
  const records = new Map<string, ExerciseRecords>();
  const oldestFirst = [...workouts].sort((a, b) => String(a.performed_on ?? "").localeCompare(String(b.performed_on ?? "")));
  for (const workout of oldestFirst) {
    const on = String(workout.performed_on ?? "");
    // Accumulated per workout before merging, because one exercise can appear in it twice and
    // the session record is what the exercise took in total that day, not per card.
    const sessions = new Map<string, number>();
    for (const exercise of workout.exercises || []) {
      const key = String(exercise.name ?? "").trim().toLowerCase();
      if (!key) continue;
      const done = (exercise.sets || []).filter(countsAsWork);
      // An exercise that only ever appears as skipped or warm-up rows is not history: leaving a
      // zeroed map entry would make the first real set look like it broke every record.
      if (!done.length) continue;
      const standing = records.get(key) ?? { setVolume: { volume: 0 }, sessionVolume: { volume: 0 } };
      const best = done.reduce((carry, set) => better(carry, Number(set.weight) || 0, Number(set.reps) || 0), standing.best);
      const topSet = done.reduce((top, set) => Math.max(top, volumeOf(set)), standing.setVolume.volume);
      records.set(key, {
        ...standing,
        // A record is stamped with this workout only when this workout is what moved it, which
        // is exactly when the fold hands back something other than what it was given.
        best: best === standing.best ? best : { ...best!, on },
        setVolume: topSet > standing.setVolume.volume ? { volume: topSet, on } : standing.setVolume
      });
      sessions.set(key, (sessions.get(key) ?? 0) + done.reduce((sum, set) => sum + volumeOf(set), 0));
    }
    for (const [key, volume] of sessions) {
      const standing = records.get(key);
      if (standing && volume > standing.sessionVolume.volume) records.set(key, { ...standing, sessionVolume: { volume, on } });
    }
  }
  return records;
}

/**
 * Which records the set at `index` would break if it were ticked off right now.
 *
 * No records at all means the exercise has no history, and a first-ever set is not an
 * achievement: counting it would make every set of a new exercise one.
 *
 * Every bar is raised by what has already been ticked off today, so beating your best and
 * then repeating it is one record rather than two. The session total is the exception that
 * needs saying out loud: once it is past, it stays past, so only the set that carries it over
 * the line counts — otherwise every remaining set of the exercise would be a record too.
 */
export function recordsBroken(records: ExerciseRecords | undefined, sets: LoggedSet[], index: number): RecordKind[] {
  const set = sets[index];
  if (!records || !set || isWarmup(set)) return [];
  const weight = Number(set.weight) || 0;
  const reps = Number(set.reps) || 0;
  if (weight <= 0 || reps <= 0) return [];

  const done = sets.filter((other, position) => position !== index && countsAsWork(other));
  const best = done.reduce((carry, other) => better(carry, Number(other.weight) || 0, Number(other.reps) || 0), records.best);
  const topSet = done.reduce((top, other) => Math.max(top, volumeOf(other)), records.setVolume.volume);
  const soFar = done.reduce((sum, other) => sum + volumeOf(other), 0);
  const volume = weight * reps;

  const broken: RecordKind[] = [];
  // Heavier is heavier, whatever the reps: one rep at a weight never lifted before is the
  // record, and holding the standing top weight for longer is a separate claim, not a
  // lighter version of the same one.
  if (!best || weight > best.weight) broken.push("weight");
  else if (weight === best.weight && reps > best.reps) broken.push("reps");
  if (volume > topSet) broken.push("set volume");
  if (soFar <= records.sessionVolume.volume && soFar + volume > records.sessionVolume.volume) broken.push("exercise volume");
  return broken;
}

/** Toast copy for the records a set just broke — short enough to read between sets. */
export function recordToast(kinds: RecordKind[]): string {
  if (!kinds.length) return "";
  if (kinds.length === 1) return `New ${kinds[0]} record`;
  if (kinds.length === 2) return `New ${kinds[0]} and ${kinds[1]} records`;
  return `New ${kinds.slice(0, -1).join(", ")}, and ${kinds.at(-1)} records`;
}

export const shortDate = (value: string) => `${Number(value.slice(5, 7))}/${Number(value.slice(8, 10))}`;

/**
 * Labels for a bar chart's x-axis, thinned so they cannot collide.
 * Anchored to the end so the most recent bar is always labelled; the rest get "".
 */
export function chartTicks(dates: string[], maxLabels = 6): string[] {
  const step = Math.max(1, Math.ceil(dates.length / Math.max(1, maxLabels)));
  return dates.map((date, index) => (dates.length - 1 - index) % step === 0 ? shortDate(date) : "");
}

/** "8-12" -> the reps to pre-fill; a bare "10" fills 10, a range fills its lower bound. */
export function repTarget(range: string): string {
  const first = range.match(/\d+/)?.[0];
  return first ?? "";
}

/** Positional diff. Renaming an exercise orphans its history, because past workouts are matched by name. */
export function renamedExercises(before: string[], after: string[]): Array<{ from: string; to: string }> {
  return before.map((from, index) => ({ from, to: after[index] || "" }))
    .filter((pair) => pair.to && pair.from.toLowerCase() !== pair.to.toLowerCase());
}

export function restReducer(state: RestState, action: RestAction): RestState {
  switch (action.type) {
    // Re-read the deadline. Dispatched on a short interval and again whenever the app comes
    // back to the foreground, which is the point at which a suspended page learns the truth.
    case "sync": {
      if (!state.endsAt) return state;
      const remaining = secondsUntil(state.endsAt, action.now);
      if (remaining === 0) return { ...state, endsAt: 0, remaining: 0 };
      // Returning the same object keeps a per-second render out of a four-times-a-second tick.
      return remaining === state.remaining ? state : { ...state, remaining };
    }
    // Each exercise carries its own rest interval, so a set completion can retarget the timer.
    case "start": {
      const target = clampRest(action.target ?? state.target);
      return { target, remaining: target, endsAt: action.now + target * 1000 };
    }
    case "toggle": {
      if (state.endsAt) return { ...state, endsAt: 0, remaining: secondsUntil(state.endsAt, action.now) };
      const remaining = state.remaining || state.target;
      return { ...state, remaining, endsAt: action.now + remaining * 1000 };
    }
    case "reset": return { ...state, endsAt: 0, remaining: state.target };
    case "adjust": {
      const target = clampRest(state.target + action.delta);
      // The clamped difference, not the requested one: at the 10-minute ceiling, +15 must move
      // neither the target nor the deadline, or the two drift apart.
      const applied = target - state.target;
      if (!state.endsAt) return { target, endsAt: 0, remaining: target };
      const endsAt = state.endsAt + applied * 1000;
      const remaining = secondsUntil(endsAt, action.now);
      // −15 with less than that left ends the rest, rather than leaving a deadline in the past.
      return remaining === 0 ? { target, endsAt: 0, remaining: 0 } : { target, endsAt, remaining };
    }
  }
}

/**
 * The timer as it was left, from whatever is in storage.
 *
 * Builds before this stored the bare target as a number, so that shape is still read.
 * An unparseable or absent value is not an error worth surfacing — it means a first run.
 */
export function restFromStorage(raw: string | null, now: number): RestState {
  const fresh = (target: number): RestState => ({ target, endsAt: 0, remaining: target });
  if (!raw) return fresh(REST_DEFAULT);
  let stored: unknown;
  try { stored = JSON.parse(raw); } catch { stored = Number(raw); }
  if (typeof stored === "number") return fresh(Number.isFinite(stored) && stored > 0 ? clampRest(stored) : REST_DEFAULT);
  if (!stored || typeof stored !== "object") return fresh(REST_DEFAULT);
  const held = stored as { target?: unknown; endsAt?: unknown };
  const target = Number(held.target) > 0 ? clampRest(Number(held.target)) : REST_DEFAULT;
  const endsAt = Number(held.endsAt);
  if (!Number.isFinite(endsAt) || endsAt <= 0) return fresh(target);
  const remaining = secondsUntil(endsAt, now);
  if (remaining > 0) return { target, endsAt, remaining };
  // Elapsed while the app was gone: ring if it only just happened, otherwise start over.
  return now - endsAt <= REST_RESUME_GRACE_MS ? { target, endsAt: 0, remaining: 0 } : fresh(target);
}

/** Only the target and the deadline are worth keeping; `remaining` is derived from them. */
export const restToStorage = (state: RestState) => JSON.stringify({ target: state.target, endsAt: state.endsAt });

/**
 * How late the page may be in noticing a deadline and still count as having watched it pass.
 * The countdown syncs four times a second, so a page that was awake notices within a fraction
 * of a second; anything beyond this was a page frozen in the background, waking up late.
 */
export const REST_NOTICE_WINDOW_MS = 2_000;

/**
 * Whether the page itself saw the rest end, as opposed to waking up to find it long over.
 *
 * This is what decides who owns the "Rest is over" banner. A page that watched the deadline
 * pass posts its own, because it may be the only alert that lands — the server may be
 * unreachable. A page that was frozen through the deadline did not: the push already raised
 * that banner at the second, and posting an identical one on re-entry is the duplicate alert
 * this guards against.
 *
 * The evidence is how late we are noticing, and deliberately not which resume event fired.
 * `visibilitychange`, `pageshow` and `focus` arrive inconsistently across iOS standalone
 * PWAs, and a thawed page's own 250ms sync can flip the timer over before any of the three
 * lands, so suppression keyed on an event always has a path that misses it. Elapsed time is
 * there to be read whenever the banner is about to be posted, whatever woke the page.
 *
 * A deadline of 0 means none was ever recorded, and a `now` before the deadline means the
 * rest was ended by hand rather than by the clock. Both are someone looking at the app, so
 * both count as watched.
 */
export const sawRestEnd = (deadline: number, now: number) => !deadline || now - deadline < REST_NOTICE_WINDOW_MS;

/**
 * An in-progress workout used to live only in React state on the Train tab. Leaving for
 * Records (or Progress/Settings) unmounted Training and wiped every set. Same fate on a
 * cold start mid-session. Persist the draft the way the rest timer already is.
 */
export const ACTIVE_WORKOUT_KEY = "chadlite-active-workout";

export type ActiveWorkoutSnapshot = { id: string; notes: string; workout: unknown };

export function activeWorkoutFromStorage(raw: string | null): ActiveWorkoutSnapshot | null {
  if (!raw) return null;
  try {
    const held = JSON.parse(raw) as { id?: unknown; notes?: unknown; workout?: unknown };
    if (!held || typeof held !== "object" || !held.workout || typeof held.id !== "string" || !held.id) return null;
    return { id: held.id, notes: typeof held.notes === "string" ? held.notes : "", workout: held.workout };
  } catch {
    return null;
  }
}

export const activeWorkoutToStorage = (snapshot: ActiveWorkoutSnapshot) => JSON.stringify(snapshot);

export const clock = (seconds: number) => `${String(Math.floor(Math.max(0, seconds) / 60)).padStart(2, "0")}:${String(Math.max(0, seconds) % 60).padStart(2, "0")}`;
